import { CustomerPortalLogo } from '../components/CustomerPortalLogo';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { resendPublicApi, type ResendRequestData } from '../lib/publicApi';
import { Package, MapPin, CreditCard, CheckCircle, AlertTriangle, Clock, Loader2, ArrowLeft, Lock, Pencil, Save, X } from 'lucide-react';

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

type PageState = 'loading' | 'form' | 'expired' | 'paid' | 'completed' | 'error';

export function ResendPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<PageState>('loading');
  const [data, setData] = useState<ResendRequestData | null>(null);
  const [error, setError] = useState<string>('');
  const [paying, setPaying] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState<Record<string, string>>({});

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const result = await resendPublicApi.getResendRequest(token);
      setData(result);

      if (result.status === 'COMPLETED') {
        setState('completed');
      } else if (result.status === 'PAID') {
        setState('paid');
      } else if (result.status === 'PAYMENT_STARTED') {
        // Payment in progress - poll for completion
        setState('paid');
      } else {
        setState('form');
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Błąd ładowania'
        : 'Błąd ładowania';
      setError(msg);
      if (msg.includes('wygasł') || msg.includes('unieważniony')) {
        setState('expired');
      } else {
        setState('error');
      }
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll for status change if payment was started
  useEffect(() => {
    if (state !== 'paid' || !token) return;
    const interval = setInterval(async () => {
      try {
        const result = await resendPublicApi.getResendRequest(token);
        setData(result);
        if (result.status === 'COMPLETED') {
          setState('completed');
        } else if (result.status === 'PENDING') {
          setState('form');
        } else if (result.status === 'PAID') {
          setState('paid');
        }
      } catch {
        // Ignore polling errors
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [state, token]);

  // Handle payment return
  useEffect(() => {
    if (searchParams.get('payment') === 'return') {
      setState('paid');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!editingAddress) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setEditingAddress(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [editingAddress]);

  const startAddressEdit = () => {
    if (!data) return;
    setAddressDraft({
      ...data.shippingAddress,
      ...(data.paczkomatCode ? { paczkomat_code: data.paczkomatCode } : {}),
    });
    setError('');
    setEditingAddress(true);
  };

  const updateAddressField = (field: string, value: string) => {
    setAddressDraft(current => ({ ...current, [field]: value }));
  };

  const handleAddressSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;
    setSavingAddress(true);
    setError('');
    try {
      const result = await resendPublicApi.updateAddress(token, {
        shippingAddress: {
          ...addressDraft,
          country: (addressDraft.country || 'PL').trim().toUpperCase(),
        },
      });
      setData(current => current ? {
        ...current,
        shippingAddress: result.shippingAddress,
        shippingMethod: result.shippingMethod,
        paczkomatCode: result.shippingAddress.paczkomat_code || current.paczkomatCode,
      } : current);
      setEditingAddress(false);
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się zapisać adresu'
        : 'Nie udało się zapisać adresu';
      setError(message);
    } finally {
      setSavingAddress(false);
    }
  };

  const handlePay = async () => {
    if (!token) return;
    setPaying(true);
    setError('');
    try {
      const result = await resendPublicApi.initiatePayment(token);
      window.location.href = result.redirectUrl;
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Błąd płatności'
        : 'Błąd płatności';
      setError(msg);
      setPaying(false);
    }
  };

  const branding = data?.branding;
  const bgColor = branding?.backgroundColor || '#1a1a2e';
  const accentColor = branding?.accentColor || '#00D4FF';
  const fontColor = branding?.fontColor || '#ffffff';
  const buttonFontColor = branding?.buttonFontColor || '#ffffff';
  const usesLightBranding = ['#f5f5f7', '#ffffff', '#f8fafc'].includes(bgColor.toLowerCase());
  const cardColor = branding?.cardColor || (usesLightBranding ? '#ffffff' : '#20222f');
  const separatorColor = branding?.separatorColor || (usesLightBranding ? '#d7dde6' : '#35384a');
  const buttonColor = branding?.buttonColor || (usesLightBranding ? '#f1f5f9' : '#292c3d');
  const buttonHoverColor = branding?.buttonHoverColor || (usesLightBranding ? '#e2e8f0' : '#34384d');

  return (
    <div
      className="min-h-screen"
      data-portal=""
      style={{
        backgroundColor: bgColor,
        color: fontColor,
        '--portal-card-bg': cardColor,
        '--portal-separator': separatorColor,
        '--portal-button': buttonColor,
        '--portal-button-hover': buttonHoverColor,
        '--portal-font': fontColor,
        '--portal-font-muted': hexToRgba(fontColor, 0.6),
        '--portal-font-dim': hexToRgba(fontColor, 0.3),
        '--portal-input-border': hexToRgba(fontColor, 0.15),
        '--portal-input-border-focus': hexToRgba(fontColor, 0.35),
      } as React.CSSProperties}
    >
      <style>{`
        [data-portal] .bg-white\\/10,
        [data-portal] .bg-white\\/5,
        [data-portal] .bg-surface-hover\\/60 { background-color: var(--portal-card-bg) !important; }
        [data-portal] .border-white\\/10,
        [data-portal] .border-white\\/5,
        [data-portal] .border-border,
        [data-portal] .border-border-subtle { border-color: var(--portal-separator) !important; }
        [data-portal] .border-white\\/20 { border-color: var(--portal-input-border) !important; }
        [data-portal] .text-text-primary { color: var(--portal-font) !important; }
        [data-portal] .text-text-primary\\/60,
        [data-portal] .text-text-primary\\/80 { color: var(--portal-font-muted) !important; }
        [data-portal] .text-text-primary\\/30,
        [data-portal] .text-text-primary\\/40 { color: var(--portal-font-dim) !important; }
        [data-portal] .placeholder-white\\/30::placeholder { color: var(--portal-font-dim) !important; }
        [data-portal] .hover\\:bg-white\\/10:hover { background-color: var(--portal-button-hover) !important; }
        [data-portal] .focus\\:border-white\\/40:focus { border-color: var(--portal-input-border-focus) !important; }
        [data-portal] .portal-secondary-button { background-color: var(--portal-button); border-color: var(--portal-separator); }
        [data-portal] .portal-secondary-button:hover { background-color: var(--portal-button-hover); }
      `}</style>
      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Header / Branding */}
        <div className="text-center mb-8 relative">
          <button
            onClick={() => window.history.back()}
            className="absolute left-0 top-0 p-2 rounded-lg hover:bg-surface-hover/60 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: fontColor, opacity: 0.6 }} />
          </button>
          {branding?.logoUrl && !logoError ? (
            <CustomerPortalLogo branding={branding} onError={() => setLogoError(true)} />
          ) : branding?.storeName ? (
            <div
              className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-text-primary"
              style={{ backgroundColor: accentColor }}
            >
              {branding.storeName.charAt(0).toUpperCase()}
            </div>
          ) : null}
          <p className="text-text-primary/60 mt-1 text-sm">Ponowna wysyłka</p>
          {data?.order?.orderNumber && (
            <p className="text-text-primary/40 mt-0.5 text-sm">Zamówienie #{data.order.orderNumber}</p>
          )}
        </div>

        {/* Content */}
        <div
          className="backdrop-blur-sm rounded-xl border p-6"
          style={{ backgroundColor: cardColor, borderColor: separatorColor }}
        >
          {state === 'loading' && (
            <div className="flex flex-col items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: accentColor }} />
              <p className="text-text-primary/60 mt-4">Ładowanie...</p>
            </div>
          )}

          {state === 'expired' && (
            <div className="flex flex-col items-center py-12">
              <Clock className="w-12 h-12 text-warning mb-4" />
              <h2 className="text-xl font-semibold text-text-primary mb-2">Link wygasł</h2>
              <p className="text-text-primary/60 text-center">
                {error || 'Ten link ponownej wysyłki wygasł lub został unieważniony. Skontaktuj się ze sklepem w celu uzyskania nowego linku.'}
              </p>
            </div>
          )}

          {state === 'error' && (
            <div className="flex flex-col items-center py-12">
              <AlertTriangle className="w-12 h-12 text-danger mb-4" />
              <h2 className="text-xl font-semibold text-text-primary mb-2">Błąd</h2>
              <p className="text-text-primary/60 text-center">{error}</p>
            </div>
          )}

          {state === 'completed' && (
            <div className="flex flex-col items-center py-12">
              <CheckCircle className="w-12 h-12 mb-4" style={{ color: accentColor }} />
              <h2 className="text-xl font-semibold text-text-primary mb-2">Płatność potwierdzona!</h2>
              <p className="text-text-primary/60 text-center">
                Twoje zamówienie ponownej wysyłki zostało utworzone. Wkrótce wyślemy paczkę na podany adres.
              </p>
            </div>
          )}

          {state === 'paid' && (
            <div className="flex flex-col items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mb-4" style={{ color: accentColor }} />
              <h2 className="text-xl font-semibold text-text-primary mb-2">Weryfikacja płatności...</h2>
              <p className="text-text-primary/60 text-center">
                Czekamy na potwierdzenie płatności. To może potrwać kilka sekund.
              </p>
              {data?.paymentRetryAt && (
                <p className="mt-3 text-center text-xs text-text-primary/40">
                  Jeśli płatność nie została dokończona, formularz odblokuje się automatycznie po wygaśnięciu sesji P24.
                </p>
              )}
            </div>
          )}

          {state === 'form' && data && (
            <div className="space-y-6">
              {/* Order items */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-5 h-5" style={{ color: accentColor }} />
                  <h2 className="text-lg font-semibold text-text-primary">Produkty</h2>
                </div>
                <div className="space-y-2">
                  {(data.order.items as Array<{ name: string; quantity: number; total: string }>).map((item, i) => (
                    <div key={i} className="py-2 border-b border-border-subtle last:border-0">
                      <p className="text-text-primary text-sm">{item.name}</p>
                      <p className="text-text-primary/40 text-xs">x{item.quantity}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping address */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5" style={{ color: accentColor }} />
                  <h2 className="text-lg font-semibold text-text-primary">Adres dostawy</h2>
                </div>
                {editingAddress ? (
                  <form onSubmit={handleAddressSave} className="space-y-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="text-xs text-text-primary/60">
                        Imię
                        <input required maxLength={100} value={addressDraft.first_name || ''} onChange={event => updateAddressField('first_name', event.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm text-text-primary" />
                      </label>
                      <label className="text-xs text-text-primary/60">
                        Nazwisko
                        <input required maxLength={100} value={addressDraft.last_name || ''} onChange={event => updateAddressField('last_name', event.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm text-text-primary" />
                      </label>
                    </div>
                    <label className="block text-xs text-text-primary/60">
                      Firma (opcjonalnie)
                      <input maxLength={200} value={addressDraft.company || ''} onChange={event => updateAddressField('company', event.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm text-text-primary" />
                    </label>
                    <label className="block text-xs text-text-primary/60">
                      Adres
                      <input required maxLength={255} value={addressDraft.address_1 || ''} onChange={event => updateAddressField('address_1', event.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm text-text-primary" />
                    </label>
                    <label className="block text-xs text-text-primary/60">
                      Dodatkowe dane adresowe (opcjonalnie)
                      <input maxLength={255} value={addressDraft.address_2 || ''} onChange={event => updateAddressField('address_2', event.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm text-text-primary" />
                    </label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="text-xs text-text-primary/60">
                        Kod pocztowy
                        <input required maxLength={20} value={addressDraft.postcode || ''} onChange={event => updateAddressField('postcode', event.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm text-text-primary" />
                      </label>
                      <label className="text-xs text-text-primary/60">
                        Miasto
                        <input required maxLength={120} value={addressDraft.city || ''} onChange={event => updateAddressField('city', event.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm text-text-primary" />
                      </label>
                      <label className="text-xs text-text-primary/60">
                        Kraj (kod)
                        <input required minLength={2} maxLength={2} value={addressDraft.country || 'PL'} onChange={event => updateAddressField('country', event.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm uppercase text-text-primary" />
                      </label>
                      <label className="text-xs text-text-primary/60">
                        Telefon
                        <input maxLength={40} value={addressDraft.phone || ''} onChange={event => updateAddressField('phone', event.target.value)} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm text-text-primary" />
                      </label>
                    </div>
                    {data.paczkomatCode && (
                      <label className="block text-xs text-text-primary/60">
                        Kod Paczkomatu / punktu odbioru
                        <input required maxLength={12} value={addressDraft.paczkomat_code || ''} onChange={event => updateAddressField('paczkomat_code', event.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-white/20 bg-transparent px-3 py-2 text-sm uppercase text-text-primary" />
                      </label>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button type="submit" disabled={savingAddress} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50" style={{ backgroundColor: accentColor, color: buttonFontColor }}>
                        {savingAddress ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Zapisz adres
                      </button>
                      <button type="button" onClick={() => setEditingAddress(false)} className="portal-secondary-button inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-text-primary/80 transition-colors">
                        <X className="h-4 w-4" /> Anuluj
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="text-text-primary/60 text-sm space-y-1">
                    <p>{data.shippingAddress?.first_name} {data.shippingAddress?.last_name}</p>
                    {data.shippingAddress?.company && <p>{data.shippingAddress.company}</p>}
                    <p>{data.shippingAddress?.address_1}{data.shippingAddress?.address_2 ? `, ${data.shippingAddress.address_2}` : ''}</p>
                    <p>{data.shippingAddress?.postcode} {data.shippingAddress?.city}</p>
                    {(data.shippingAddress?.phone || data.order?.customerPhone) && (
                      <p>Tel: {data.shippingAddress?.phone || data.order?.customerPhone}</p>
                    )}
                    {data.shippingMethod && <p>Dostawa: {data.shippingMethod}</p>}
                    {data.paczkomatCode && <p>Paczkomat: {data.paczkomatCode}</p>}
                    <button type="button" onClick={startAddressEdit} className="portal-secondary-button mt-3 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm text-text-primary/80 transition-colors">
                      <Pencil className="h-4 w-4" /> Zmień adres
                    </button>
                  </div>
                )}
              </div>

              {/* Payment summary */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-5 h-5" style={{ color: accentColor }} />
                  <h2 className="text-lg font-semibold text-text-primary">Do zapłaty</h2>
                </div>
                <div className="flex justify-between items-center py-2">
                  <p className="text-text-primary/80 text-sm">{data.paymentItem.name}</p>
                  <p className="text-xl font-bold text-text-primary">
                    {data.paymentItem.price.toFixed(2)} {data.currency}
                  </p>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-danger/20 border border-red-400/30 rounded-lg px-4 py-3">
                  <p className="text-danger text-sm">{error}</p>
                </div>
              )}

              {/* Pay button */}
              <button
                onClick={handlePay}
                disabled={paying || editingAddress || savingAddress}
                className="w-full py-3 rounded-xl font-semibold text-lg transition-all disabled:opacity-50"
                style={{ backgroundColor: accentColor, color: buttonFontColor }}
              >
                {paying ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Przekierowanie do płatności...
                  </span>
                ) : editingAddress ? (
                  'Najpierw zapisz adres'
                ) : (
                  `Opłać i wyślij ponownie - ${data.amount.toFixed(2)} ${data.currency}`
                )}
              </button>

              <div className="flex items-center justify-center gap-1.5 text-text-primary/30 text-xs">
                <Lock className="w-3 h-3" />
                <span>Bezpieczna płatność szyfrowana SSL - Przelewy24</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-text-primary/20 text-xs text-center mt-8">
          Powered by EcomFlow
        </p>
      </div>
    </div>
  );
}
